import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { 
  FileText, 
  Clock, 
  Package, 
  CheckCircle, 
  Truck, 
  Warning,
  CalendarCheck,
  Timer,
  Lightning,
  ChartBar,
  Wrench,
  HouseLine,
  XCircle,
  Pause,
  X
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { JOSidePanel, DrillDownJobList } from '../components/JOSidePanel';
import { PendingAllocationsDialog } from '../components/admin/planning/PendingAllocationsDialog';
import { AdvanceAvailableDialog } from '../components/admin/receipts/AdvanceAvailableDialog';
import { formatAnyDate } from '../utils/dateFormat';

const CHART_TICK = { fontSize: 12 };

function formatProdTime(minutes) {
  if (!minutes) return '--';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
}

function StatCard({ label, value, icon: Icon, color, onClick, active }) {
  return (
    <Card
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`border-zinc-300 rounded-sm shadow-sm transition-all ${
        onClick ? 'cursor-pointer hover:border-orange-400 hover:shadow-md' : ''
      } ${active ? 'border-orange-500 ring-2 ring-orange-200 bg-orange-50/30' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4 md:p-6">
        <div className="flex items-center gap-4">
          <Icon size={32} weight="bold" className={color} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className="text-3xl font-light text-zinc-950">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function UrgentRequestCard({ jo, onAction, onNavigate, actionLoading }) {
  return (
    <Card data-testid={`urgent-card-${jo.jo_number}`} className="border-red-200 bg-red-50 rounded-sm">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-zinc-950">JO #{jo.jo_number}</h3>
            <Badge className="bg-red-100 text-red-800 border border-red-300">Pending</Badge>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-zinc-500 text-xs font-bold uppercase">Customer:</span>
              <p className="text-zinc-950">{jo.customer_name}</p>
            </div>
            <div>
              <span className="text-zinc-500 text-xs font-bold uppercase">Current Delivery:</span>
              <p className="text-zinc-950">{formatAnyDate(jo.delivery_date)}</p>
            </div>
            <div>
              <span className="text-zinc-500 text-xs font-bold uppercase">Requested Delivery:</span>
              <p className="text-red-700 font-bold">{formatAnyDate(jo.urgent_request?.requested_delivery_date)}</p>
            </div>
            <div>
              <span className="text-zinc-500 text-xs font-bold uppercase">Requested By:</span>
              <p className="text-zinc-950">{jo.urgent_request?.requested_by}</p>
            </div>
          </div>
          <div>
            <span className="text-zinc-500 text-xs font-bold uppercase">Reason:</span>
            <p className="text-zinc-950 text-sm">{jo.urgent_request?.reason}</p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid={`approve-urgent-${jo.jo_number}`}
              onClick={() => onAction(jo.jo_number, 'approve')}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
            >
              {actionLoading ? 'Processing...' : 'Approve'}
            </Button>
            <Button
              data-testid={`reject-urgent-${jo.jo_number}`}
              onClick={() => onAction(jo.jo_number, 'reject')}
              disabled={actionLoading}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 flex-1"
            >
              Reject
            </Button>
            <Button
              variant="outline"
              onClick={() => onNavigate(`/job-orders/${jo.jo_number}`)}
              className="flex-1"
            >
              View Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductivitySection({ stats }) {
  return (
    <Card data-testid="production-productivity-card" className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
          <Timer size={22} weight="bold" className="text-purple-600" />
          Production Productivity - Today
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Lightning, color: 'purple', label: 'Completed Today', value: stats?.prod_completed_today || 0, testId: 'prod-completed-today' },
            { icon: ChartBar, color: 'orange', label: 'Windows Today', value: stats?.prod_windows_today || 0, testId: 'prod-windows-today' },
            { icon: ChartBar, color: 'cyan', label: 'Sqft Today', value: stats?.prod_sqft_today || 0, testId: 'prod-sqft-today' },
            { icon: Clock, color: 'emerald', label: 'Avg Time', value: formatProdTime(stats?.prod_avg_time_minutes), testId: 'prod-avg-time' }
          ].map(({ icon: Ic, color, label, value, testId }) => (
            <div key={testId} className={`bg-${color}-50 border border-${color}-200 rounded-sm p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Ic size={18} weight="bold" className={`text-${color}-600`} />
                <p className={`text-xs font-bold uppercase tracking-[0.2em] text-${color}-600`}>{label}</p>
              </div>
              <p data-testid={testId} className="text-3xl font-light text-zinc-950">{value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function VolumeChart({ volumeData }) {
  if (volumeData.length === 0) return null;
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950">Production Volume (Last 30 Days)</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div style={{ height: 300, width: '100%' }}>
          <ResponsiveContainer>
            <BarChart data={volumeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="_id" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} />
              <Tooltip />
              <Bar dataKey="windows" fill="#FF6B35" name="Windows" />
              <Bar dataKey="sqft" fill="#2D3436" name="Sqft" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardTotals({ stats }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
      <CardContent className="p-6">
        <div className="flex items-center justify-around flex-wrap gap-6">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows Completed</p>
            <p data-testid="dashboard-total-windows" className="text-4xl font-light text-zinc-950">{stats?.total_windows_completed || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft Completed</p>
            <p data-testid="dashboard-total-sqft" className="text-4xl font-light text-zinc-950">{(stats?.total_sqft_completed || 0).toFixed(2)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function UrgentRequestsDialog({ open, onOpenChange, requests, onAction, onNavigate, actionLoading }) {
  if (!open || requests.length === 0) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Warning size={24} weight="bold" />
            Urgent Delivery Requests ({requests.length})
          </DialogTitle>
          <DialogDescription className="sr-only">Review and approve or reject urgent delivery requests</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {requests.map((jo) => (
            <UrgentRequestCard key={jo.jo_number} jo={jo} onAction={onAction} onNavigate={onNavigate} actionLoading={actionLoading} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [volumeData, setVolumeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [urgentRequests, setUrgentRequests] = useState([]);
  const [showUrgentPopup, setShowUrgentPopup] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [drillDown, setDrillDown] = useState(null); // { label, statusFilter }
  const [sidePanelJO, setSidePanelJO] = useState(null);
  const [showPendingAlloc, setShowPendingAlloc] = useState(false);
  const [advanceAlerts, setAdvanceAlerts] = useState(null);
  const drillDownRef = useRef(null);

  useEffect(() => {
    // Pending production allocation popup — once per session on admin login
    if (sessionStorage.getItem('pp_login_popup_shown')) return;
    api.get('/api/admin/production-planning/pending')
      .then(({ data }) => {
        if ((data?.total || 0) > 0) {
          sessionStorage.setItem('pp_login_popup_shown', '1');
          setShowPendingAlloc(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Customer advance available popup — once per session
    if (sessionStorage.getItem('adv_alert_shown')) return;
    api.get('/api/admin/receipt-allocation/advance-conversion-alerts')
      .then(({ data }) => {
        if ((data?.total || 0) > 0) {
          sessionStorage.setItem('adv_alert_shown', '1');
          setAdvanceAlerts(data.alerts);
        }
      })
      .catch(() => {});
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [statsRes, volumeRes, urgentRes, scheduledRes] = await Promise.all([
        api.get(`/api/dashboard/stats`),
        api.get(`/api/dashboard/production-volume`),
        api.get(`/api/urgent-requests`),
        api.get(`/api/job-orders?status=scheduled_for_dispatch`),
      ]);
      setStats(statsRes.data);
      setVolumeData(volumeRes.data);
      setUrgentRequests(urgentRes.data);
      setScheduledJobs(scheduledRes.data || []);
      
      if (urgentRes.data.length > 0) {
        setShowUrgentPopup(true);
      }
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load dashboard data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleUrgentAction = useCallback(async (jo_number, action) => {
    setActionLoading(true);
    try {
      await api.post(
        `/api/job-orders/${jo_number}/urgent-request/${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Urgent request ${action}d`);
      fetchDashboardData();
    } catch {
      toast.error(`Failed to ${action} urgent request`);
    } finally {
      setActionLoading(false);
    }
  }, [fetchDashboardData]);

  const [drillDownJobs, setDrillDownJobs] = useState([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  const handleCardClick = useCallback(async (label, statusFilter) => {
    // Toggle off if clicking the same card
    if (drillDown?.statusFilter === statusFilter) {
      setDrillDown(null);
      setDrillDownJobs([]);
      setSidePanelJO(null);
      return;
    }
    setDrillDown({ label, statusFilter });
    setSidePanelJO(null);
    setDrillDownLoading(true);
    try {
      const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
      const results = await Promise.all(
        statuses.map(s => api.get(`/api/job-orders?status=${s}`))
      );
      const jobs = results.flatMap(r => r.data || []);
      setDrillDownJobs(jobs);
      setTimeout(() => drillDownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch {
      setDrillDownJobs([]);
    } finally {
      setDrillDownLoading(false);
    }
  }, [drillDown]);

  const statCards = useMemo(() => [
    { label: 'Total Jobs', value: stats?.total_jobs || 0, icon: FileText, color: 'text-blue-600', statusFilter: null },
    { label: 'Pending Production', value: stats?.pending_production || 0, icon: Clock, color: 'text-amber-600', statusFilter: ['pending_procurement', 'procurement_approved'] },
    { label: 'Rejected', value: stats?.procurement_rejected || 0, icon: XCircle, color: 'text-red-600', statusFilter: 'procurement_rejected' },
    { label: 'Under Production', value: stats?.under_production || 0, icon: Package, color: 'text-purple-600', statusFilter: 'under_production' },
    { label: 'On Hold', value: stats?.production_on_hold || 0, icon: Pause, color: 'text-amber-600', statusFilter: 'production_on_hold' },
    { label: 'Ready to Dispatch', value: stats?.production_completed || 0, icon: CheckCircle, color: 'text-emerald-600', statusFilter: 'production_completed' },
    { label: 'Scheduled Dispatch', value: stats?.scheduled_for_dispatch || 0, icon: CalendarCheck, color: 'text-cyan-600', statusFilter: 'scheduled_for_dispatch' },
    { label: 'Partial Dispatch', value: stats?.partially_dispatched || 0, icon: Truck, color: 'text-orange-600', statusFilter: 'partially_dispatched' },
    { label: 'Dispatched', value: stats?.dispatched || 0, icon: Truck, color: 'text-zinc-600', statusFilter: 'dispatched' },
    { label: 'Pending Installation', value: stats?.pending_for_installation || 0, icon: Wrench, color: 'text-indigo-600', statusFilter: 'pending_for_installation' },
    { label: 'Installation Done', value: stats?.installation_completed || 0, icon: HouseLine, color: 'text-teal-600', statusFilter: 'installation_completed' },
    { label: 'Exec Dispatch Req', value: stats?.pending_install_requests || 0, icon: Warning, color: 'text-red-600', statusFilter: null },
  ], [stats]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading dashboard...</div>
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout>
        <div className="text-center py-16 space-y-4">
          <p className="text-lg font-bold text-red-500">Failed to load dashboard</p>
          <p className="text-sm text-zinc-400">There was an issue loading data. Please try again.</p>
          <Button onClick={fetchDashboardData} className="bg-blue-600 hover:bg-blue-700 text-white font-bold">
            Retry
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-2">Production overview and statistics</p>
          </div>
          {urgentRequests.length > 0 && (
            <Button
              data-testid="urgent-alert-button"
              onClick={() => setShowUrgentPopup(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold h-11 animate-pulse"
            >
              <Warning size={20} weight="bold" className="mr-2" />
              {urgentRequests.length} Urgent Request{urgentRequests.length > 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {/* Urgent Alert Banner */}
        {urgentRequests.length > 0 && (
          <Card className="border-red-400 bg-red-50 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Warning size={28} weight="bold" className="text-red-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold text-red-800">{urgentRequests.length} Pending Urgent Request{urgentRequests.length > 1 ? 's' : ''}</p>
                  <p className="text-sm text-red-600">Click to review and approve/reject urgent delivery requests</p>
                </div>
                <Button
                  data-testid="review-urgent-button"
                  onClick={() => setShowUrgentPopup(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Review Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map((card) => (
            <StatCard
              key={card.label}
              label={card.label}
              value={card.value}
              icon={card.icon}
              color={card.color}
              onClick={card.statusFilter ? () => handleCardClick(card.label, card.statusFilter) : undefined}
              active={drillDown?.label === card.label}
            />
          ))}
        </div>

        {/* Drill-Down Job List */}
        {drillDown && (
          <Card ref={drillDownRef} data-testid="dashboard-drill-down" className="border-orange-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-orange-200 bg-orange-50/50 py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-zinc-950">
                  {drillDown.label} ({drillDownLoading ? '...' : drillDownJobs.length})
                </CardTitle>
                <Button
                  data-testid="close-drill-down"
                  variant="ghost"
                  size="sm"
                  onClick={() => setDrillDown(null)}
                  className="h-8 w-8 p-0"
                >
                  <X size={18} weight="bold" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <DrillDownJobList
                jobs={drillDownJobs}
                title={drillDown.label}
                onSelectJO={setSidePanelJO}
              />
            </CardContent>
          </Card>
        )}

        <DashboardTotals stats={stats} />

        {/* Scheduled for Dispatch List */}
        {scheduledJobs.length > 0 && (
          <Card data-testid="scheduled-dispatch-card" className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300">
              <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
                <CalendarCheck size={22} weight="bold" className="text-cyan-600" />
                Scheduled for Dispatch ({scheduledJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Schedule Date</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Driver / Vehicle</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledJobs.map(jo => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/job-orders/${jo.jo_number}`)}>
                        <td className="p-3 font-bold text-zinc-950 hover:text-orange-600">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700">{jo.work_location}</td>
                        <td className="p-3 text-yellow-700 font-bold">{jo.dispatch_schedule_date}</td>
                        <td className="p-3 text-center text-zinc-600 text-xs">{jo.scheduled_driver_name || '-'} / {jo.scheduled_vehicle_number || '-'}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.no_of_windows || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <ProductivitySection stats={stats} />
        <VolumeChart volumeData={volumeData} />
      </div>

      <UrgentRequestsDialog
        open={showUrgentPopup}
        onOpenChange={setShowUrgentPopup}
        requests={urgentRequests}
        onAction={handleUrgentAction}
        onNavigate={navigate}
        actionLoading={actionLoading}
      />

      <JOSidePanel
        open={!!sidePanelJO}
        onClose={() => setSidePanelJO(null)}
        jobOrder={sidePanelJO}
      />

      <PendingAllocationsDialog
        open={showPendingAlloc}
        onOpenChange={setShowPendingAlloc}
        onAssign={(jn) => navigate(`/admin/production-planning?assign=${jn}`)}
      />

      <AdvanceAvailableDialog
        open={!!advanceAlerts && !showPendingAlloc}
        onOpenChange={(o) => !o && setAdvanceAlerts(null)}
        alerts={advanceAlerts || []}
      />
    </Layout>
  );
}
